<script context="module">
  const handlers = [];
  let manager;

  if (typeof window !== "undefined") {
    const run_all = () => handlers.forEach((fn) => fn());

    window.addEventListener("scroll", run_all);
    window.addEventListener("resize", run_all);
  }

  if (typeof IntersectionObserver !== "undefined") {
    const map = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const update = map.get(entry.target);
          const index = handlers.indexOf(update);

          if (entry.isIntersecting) {
            if (index === -1) handlers.push(update);
          } else {
            update();
            if (index !== -1) handlers.splice(index, 1);
          }
        });
      },
      {
        rootMargin: "400px 0px",
      },
    );

    manager = {
      add: ({ outer, update }) => {
        const { top, bottom } = outer.getBoundingClientRect();

        if (top < window.innerHeight && bottom > 0) handlers.push(update);

        map.set(outer, update);
        observer.observe(outer);
      },

      remove: ({ outer, update }) => {
        const index = handlers.indexOf(update);
        if (index !== -1) handlers.splice(index, 1);

        map.delete(outer);
        observer.unobserve(outer);
      },
    };
  } else {
    manager = {
      add: ({ update }) => {
        handlers.push(update);
      },

      remove: ({ update }) => {
        const index = handlers.indexOf(update);
        if (index !== -1) handlers.splice(index, 1);
      },
    };
  }
</script>

<script>
  import { onMount } from "svelte";

  export let top = 0;
  export let bottom = 1;
  export let threshold = 0.5;
  export let query = "section";
  export let parallax = false;

  export let index = 0;
  export let count = 0;
  export let offset = 0;
  export let progress = 0;
  export let visible = false;

  let outer;
  let foreground;
  let background;
  let sections;
  let wh = 0;
  let width = 1;
  let height = 0;
  let backgroundHeight = 0;
  let inverted;
  let translateY = 0;

  $: top_px = Math.round(top * wh);
  $: bottom_px = Math.round(bottom * wh);
  $: threshold_px = Math.round(threshold * wh);

  $: (top, bottom, threshold, parallax, update());

  $: backgroundStyle = `
    position: sticky;
    top: ${top_px}px;
    width: ${width}px;
    transform: translate(0, ${translateY}px);
    z-index: ${inverted ? 3 : 1};
  `;

  onMount(() => {
    sections = foreground.querySelectorAll(query);
    count = sections.length;

    update();

    const scroller = { outer, update };
    manager.add(scroller);

    return () => manager.remove(scroller);
  });

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function update() {
    if (!foreground || !background || !outer || wh === 0) return;

    const outerRect = outer.getBoundingClientRect();
    width = outerRect.width;
    height = outerRect.height;

    const fg = foreground.getBoundingClientRect();
    const bg = background.getBoundingClientRect();

    visible = fg.top < wh && fg.bottom > 0;

    const foreground_height = fg.height;
    backgroundHeight = bg.height;
    const available_space = bottom_px - top_px;
    const denominator = foreground_height - available_space;

    progress = denominator === 0 ? 0 : (top_px - fg.top) / denominator;

    const clampedProgress = clamp(progress, 0, 1);
    translateY = parallax ? -Math.round(clampedProgress * (backgroundHeight - available_space)) : 0;

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const { top } = section.getBoundingClientRect();

      const next = sections[i + 1];
      const bottom = next ? next.getBoundingClientRect().top : fg.bottom;

      offset = (threshold_px - top) / (bottom - top);
      if (bottom >= threshold_px) {
        index = i;
        break;
      }
    }
  }
</script>

<svelte:window bind:innerHeight={wh} />

<svelte-scroller-outer bind:this={outer}>
  <svelte-scroller-background-track class="background-track" style:height={`${height}px`}>
    <svelte-scroller-background-container class="background-container" style={backgroundStyle}>
      <svelte-scroller-background bind:this={background}>
        <slot name="background"></slot>
      </svelte-scroller-background>
    </svelte-scroller-background-container>
  </svelte-scroller-background-track>

  <svelte-scroller-foreground bind:this={foreground}>
    <slot name="foreground"></slot>
  </svelte-scroller-foreground>
</svelte-scroller-outer>

<style>
  svelte-scroller-outer {
    display: block;
    position: relative;
  }

  svelte-scroller-background-track {
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    max-width: 100%;
    pointer-events: none;
  }

  svelte-scroller-background-container {
    display: block;
    max-width: 100%;
    will-change: transform;
  }

  svelte-scroller-background {
    display: block;
    position: relative;
    width: 100%;
  }

  svelte-scroller-foreground {
    display: block;
    position: relative;
    z-index: 2;
  }

  svelte-scroller-foreground::after {
    content: " ";
    display: block;
    clear: both;
  }
</style>
